/**
 * Pricing Helper Functions
 * Handles price calculations for different item types
 */

/**
 * Returns the effective base price, using promotional price when active.
 */
export const getEffectiveBasePrice = (item) => {
  if (item?.isPromoActive && item?.promotionalPrice > 0) return item.promotionalPrice;
  if (item?.comboConfig?.basePrice != null) return item.comboConfig.basePrice;
  return item?.basePrice ?? item?.price ?? 0;
};

/**
 * Returns the original (reference) base price regardless of promotions.
 */
export const getReferenceBasePrice = (item) => {
  if (item?.comboConfig?.basePrice != null) return item.comboConfig.basePrice;
  return item?.basePrice ?? item?.price ?? 0;
};

import { ITEM_TYPES, getItemType } from '@/constants/itemTypes';

/**
 * Calculate total price for a combo item
 * Formula: basePrice + sum(priceAdjustments for selected items)
 */
export const calculateComboPrice = (comboConfig, selections = {}) => {
  if (!comboConfig || typeof comboConfig.basePrice !== 'number') {
    console.error('Invalid combo config:', comboConfig);
    return 0;
  }

  let total = comboConfig.basePrice;

  // Add price adjustments for each selected item
  comboConfig.slots?.forEach(slot => {
    const selectedItemId = selections[slot.id];
    if (selectedItemId) {
      const selectedItem = slot.allowedItems?.find(item => item.itemId === selectedItemId);
      if (selectedItem && typeof selectedItem.priceAdjustment === 'number') {
        total += selectedItem.priceAdjustment;
      }
    }
  });

  return total;
};

/**
 * Calculate minimum price for a combo item (for "starting from" display)
 * Formula: basePrice + sum(minimum priceAdjustments for each slot)
 */
export const calculateMinimumComboPrice = (comboConfig) => {
  if (!comboConfig || typeof comboConfig.basePrice !== 'number') {
    return null;
  }

  let total = comboConfig.basePrice;
  let hasVariations = false;

  // Add minimum price adjustment from each slot
  comboConfig.slots?.forEach(slot => {
    if (slot.type === 'modifier_group') {
      hasVariations = true;
    } else if (slot.allowedItems && slot.allowedItems.length > 0) {
      const adjustments = slot.allowedItems.map(item => item.priceAdjustment || 0);
      const minAdjustment = Math.min(...adjustments);
      const maxAdjustment = Math.max(...adjustments);

      if (minAdjustment !== maxAdjustment) {
        hasVariations = true;
      }

      total += minAdjustment;
    }
  });

  return {
    minPrice: total,
    hasVariations
  };
};

/**
 * Calculate total price for a personalized item (like pizza with multiple flavors)
 */
export const calculatePersonalizedPrice = (personalizedConfig, portionSelections = [], selectedSizeVariationId = null) => {
  if (!personalizedConfig) {
    console.error('Invalid personalized config:', personalizedConfig);
    return 0;
  }

  let total = 0;

  const sizeConfig = personalizedConfig.sizeConfigs?.find(
    config => config.variationId === selectedSizeVariationId
  );

  portionSelections.forEach(selection => {
    const portion = sizeConfig?.portions?.find(p => p.id === selection.portionId);
    const option = personalizedConfig.options?.find(o => o.id === selection.optionId);

    if (portion && option && typeof option.fullPrice === 'number' && typeof portion.fraction === 'number') {
      const portionPrice = option.fullPrice * portion.fraction;
      total += portionPrice;
    }
  });

  return total;
};

/**
 * Calculate price for a single portion of a personalized item
 */
export const calculatePortionPrice = (fullPrice, fraction) => {
  if (typeof fullPrice !== 'number' || typeof fraction !== 'number') {
    return 0;
  }
  return fullPrice * fraction;
};

/**
 * Format price adjustment for display
 */
export const formatPriceAdjustment = (amount) => {
  if (typeof amount !== 'number') {
    return 'N/A';
  }

  if (amount === 0) {
    return `+R$ 0.00`;
  }

  const sign = amount > 0 ? '+' : '';
  return `${sign}R$ ${Math.abs(amount).toFixed(2)}`;
};

/**
 * Calculate the total additional price from modifier group selections (new system).
 *
 * @param {Object} item - Menu item with modifierGroups join config
 * @param {Array}  modifierGroupSelections - [{groupId, selections:[{modifierId, quantity}]}]
 * @returns {number} Total modifier price addition
 */
export const calculateModifierGroupsPrice = (item, modifierGroupSelections = []) => {
  if (!modifierGroupSelections.length) return 0;

  const groupLinks = item.modifierGroups || [];
  let total = 0;

  for (const sel of modifierGroupSelections) {
    const groupLink = groupLinks.find(gl => gl.groupId === sel.groupId);
    const groupData = item._modifierGroupsData?.[sel.groupId]; // embedded for display
    if (!groupData) continue;

    for (const s of (sel.selections || [])) {
      const modifier = (groupData.modifiers || []).find(m => m.id === s.modifierId);
      if (!modifier) continue;
      const unitPrice = (groupLink?.priceOverrides?.[s.modifierId] !== undefined)
        ? groupLink.priceOverrides[s.modifierId]
        : modifier.basePrice;
      total += unitPrice * (s.quantity || 1);
    }
  }

  return total;
};

/**
 * Calculate price for a personalized item using the new slot-based flavor config.
 *
 * @param {Object} item - Menu item with personalizedFlavorConfig
 * @param {string} selectedSizeId - ID of the selected size variation
 * @param {Array}  selectedFlavors - [{modifierId}] — one per slot
 * @returns {number} Total price (size price + flavor additions)
 */
export const calculatePersonalizedFlavorPrice = (item, selectedSizeId, selectedFlavors = []) => {
  const fc = item.personalizedFlavorConfig;
  if (!fc) return item.price || 0;

  const sizeVariation = item.variations?.find(v => v.id === selectedSizeId);
  const basePrice = sizeVariation?.price || item.price || 0;

  const group = item._modifierGroupsData?.[fc.flavorGroupId];
  if (!group) return basePrice;

  // Per-flavor price override (set in admin FlavorGroupSection) wins over the
  // group catalog basePrice — same precedence used by validateAndPricePersonalized.
  const flavorPrices = selectedFlavors.map(sel => {
    const override = fc.priceOverrides?.[sel.modifierId];
    if (typeof override === 'number') return override;
    const mod = (group.modifiers || []).find(m => m.id === sel.modifierId);
    return mod?.basePrice || 0;
  });

  if (!flavorPrices.length) return basePrice;

  const strategy = fc.pricingStrategy || 'soma';
  let addition = 0;
  if (strategy === 'soma') {
    addition = flavorPrices.reduce((s, p) => s + p, 0);
  } else if (strategy === 'maior_preco') {
    // Each portion is charged at the most expensive flavor's per-portion price.
    addition = Math.max(...flavorPrices) * flavorPrices.length;
  } else if (strategy === 'media') {
    addition = flavorPrices.reduce((s, p) => s + p, 0) / flavorPrices.length;
  }

  return basePrice + addition;
};

/**
 * Calculate price for a simple item with variations and modifier groups (new system).
 */
export const calculateSimpleItemPrice = (item, selectedVariation = null, modifierGroupSelections = [], quantity = 1) => {
  let basePrice = item.price || 0;

  if (selectedVariation && typeof selectedVariation.price === 'number') {
    basePrice = selectedVariation.price;
  }

  const modifiersTotal = calculateModifierGroupsPrice(item, modifierGroupSelections);

  return (basePrice + modifiersTotal) * quantity;
};

/**
 * Get price breakdown for display in cart or checkout
 */
export const getPriceBreakdown = (cartItem, menuItem) => {
  const breakdown = {
    basePrice: 0,
    additions: [],
    total: 0
  };

  if (!cartItem || !menuItem) {
    return breakdown;
  }

  switch (cartItem.itemType) {
    case 'SIMPLE':
      breakdown.basePrice = cartItem.selectedVariation?.price || menuItem.price || 0;

      if (cartItem.modifiers && cartItem.modifiers.length > 0) {
        cartItem.modifiers.forEach(modifier => {
          breakdown.additions.push({
            name: modifier.name,
            price: modifier.price
          });
        });
      }
      break;

    case 'COMBO':
      breakdown.basePrice = menuItem.comboConfig?.basePrice || 0;

      if (cartItem.comboSelections && menuItem.comboConfig?.slots) {
        menuItem.comboConfig.slots.forEach(slot => {
          const selectedItemId = cartItem.comboSelections[slot.id]?.itemId;
          if (selectedItemId) {
            const allowedItem = slot.allowedItems?.find(item => item.itemId === selectedItemId);
            if (allowedItem && allowedItem.priceAdjustment > 0) {
              breakdown.additions.push({
                name: cartItem.comboSelections[slot.id].name,
                price: allowedItem.priceAdjustment
              });
            }
          }
        });
      }
      break;

    case 'PERSONALIZED': {
      const selectedSizeVariation = menuItem.variations?.find(
        v => v.id === cartItem.personalizedSelections?.selectedSize
      );

      if (selectedSizeVariation) {
        breakdown.basePrice = selectedSizeVariation.price || 0;
      }

      if (cartItem.personalizedSelections?.portions) {
        cartItem.personalizedSelections.portions.forEach(portion => {
          breakdown.additions.push({
            name: portion.optionName,
            price: portion.portionPrice
          });
        });
      }
      break;
    }

    default:
      breakdown.basePrice = menuItem.price || 0;
  }

  const additionsTotal = breakdown.additions.reduce((sum, addition) => sum + addition.price, 0);
  breakdown.total = (breakdown.basePrice + additionsTotal) * (cartItem.quantity || 1);

  return breakdown;
};

/**
 * Validate if a combo has all required slots filled
 */
export const validateComboSelections = (comboConfig, selections = {}) => {
  const missingSlots = [];

  comboConfig?.slots?.forEach(slot => {
    if (slot.required && !selections[slot.id]) {
      missingSlots.push(slot.name || 'Unnamed slot');
    }
  });

  return {
    isValid: missingSlots.length === 0,
    missingSlots
  };
};

/**
 * Validate if a personalized item configuration is complete
 */
export const validatePersonalizedSelections = (personalizedConfig, portionSelections = [], selectedSize = null) => {
  const errors = [];

  if (!selectedSize) {
    errors.push({
      key: 'noSize',
      data: {}
    });
    return { isValid: false, errors };
  }

  const sizeConfig = personalizedConfig?.sizeConfigs?.find(config => config.variationId === selectedSize);

  if (!sizeConfig) {
    errors.push({
      key: 'invalidSize',
      data: {}
    });
    return { isValid: false, errors };
  }

  if (portionSelections.length === 0) {
    errors.push({
      key: 'noFlavors',
      data: {}
    });
  }

  // Check if all portions have been filled
  const totalPortions = sizeConfig.portions?.length || 0;
  if (totalPortions > 0 && portionSelections.length < totalPortions) {
    const missingCount = totalPortions - portionSelections.length;
    errors.push({
      key: 'allPortionsRequired',
      data: { totalPortions, missingCount }
    });
  }

  if (portionSelections.length > sizeConfig.maxFlavors) {
    errors.push({
      key: 'maxFlavorsExceeded',
      data: { maxFlavors: sizeConfig.maxFlavors }
    });
  }

  const portionIds = portionSelections.map(s => s.portionId);
  const uniquePortionIds = new Set(portionIds);
  if (portionIds.length !== uniquePortionIds.size) {
    errors.push({
      key: 'duplicatePortions',
      data: {}
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate the unit price for a cart item (price for 1 unit)
 * @param {object} item - Cart item
 * @returns {number} Unit price
 */
export const calculateUnitPrice = (item) => {
  const itemType = getItemType(item);

  // Loyalty free-item rewards: customer pays only for the modifiers added
  // on top of the base item (loyalty covers base price × quantity).
  if (item._loyaltyReward?.type === 'free_item') {
    if (itemType === ITEM_TYPES.SIMPLE) {
      // Use the stored totalPrice (already modifiers-only after FreeItemPicker
      // recomputed it) divided by quantity to derive the per-unit cost. This
      // also handles modifierGroupSelections correctly since their pricing was
      // baked into totalPrice at add-to-cart time.
      if (item.totalPrice != null && item.quantity && item.quantity > 0) {
        return item.totalPrice / item.quantity;
      }
      // Fallback: legacy flat modifiers only.
      return (item.modifiers || []).reduce((sum, mod) => sum + (mod.price ?? 0), 0);
    }
    // COMBO/PERSONALIZED: full freebie (splitting base from selections is
    // non-trivial; admin shouldn't pick complex items as free rewards anyway).
    return 0;
  }

  switch (itemType) {
    case ITEM_TYPES.SIMPLE: {
      const basePrice = item.selectedVariation ? item.selectedVariation.price : getEffectiveBasePrice(item);
      const modifiersPrice = (item.modifiers || []).reduce(
        (sum, mod) => sum + (mod.price ?? 0),
        0
      );
      return basePrice + modifiersPrice;
    }

    case ITEM_TYPES.COMBO:
    case ITEM_TYPES.PERSONALIZED:
      // For these types, totalPrice is set when adding to cart
      // Calculate unit price from total / quantity safely
      if (item.totalPrice && item.quantity && item.quantity > 0) {
        return item.totalPrice / item.quantity;
      }
      return item.price ?? 0;

    default:
      return item.price ?? 0;
  }
};

/**
 * Calculate total price for a cart item with given quantity
 * Unified function to replace duplicated price calculations
 * @param {object} item - Cart item
 * @param {number} quantity - Optional quantity override
 * @returns {number} Total price
 */
export const calculateItemTotalPrice = (item, quantity = null) => {
  const qty = quantity ?? item.quantity ?? 1;
  const unitPrice = calculateUnitPrice(item);
  return unitPrice * qty;
};

/**
 * Generate a unique key for a cart item based on its configuration
 * Used for comparing items to merge quantities
 * @param {object} item - Cart item
 * @returns {string} Unique cart item key
 */
export const generateCartItemKey = (item) => {
  const itemType = getItemType(item);
  // Loyalty rewards get a unique key suffix so a free reward line never
  // merges with a paid line of the same item.
  const rewardSuffix = item._loyaltyReward
    ? `:loyalty:${item._loyaltyReward.id || ''}`
    : '';
  const baseKey = `${item.id}${rewardSuffix}`;

  const encodeMGS = (mgs = []) =>
    [...mgs]
      .sort((a, b) => a.groupId.localeCompare(b.groupId))
      .map(g =>
        `${g.groupId}:[${[...g.selections]
          .sort((a, b) => a.modifierId.localeCompare(b.modifierId))
          .map(s => `${s.modifierId}x${s.quantity ?? 1}`)
          .join(',')}]`
      )
      .join('|');

  switch (itemType) {
    case ITEM_TYPES.SIMPLE: {
      const modifierIds = (item.modifiers || [])
        .map(m => m.id)
        .filter(Boolean)
        .sort()
        .join(',');
      const variationId = item.selectedVariation?.id || 'default';
      return `${baseKey}:simple:${variationId}:${modifierIds}:${encodeMGS(item.modifierGroupSelections)}`;
    }

    case ITEM_TYPES.COMBO: {
      const slotSelections = Object.entries(item.comboSelections || {})
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([slotId, selection]) => `${slotId}:${selection?.itemId || ''}`)
        .join('|');
      return `${baseKey}:combo:${slotSelections}:${encodeMGS(item.modifierGroupSelections)}`;
    }

    case ITEM_TYPES.PERSONALIZED: {
      const flavors = [...(item.personalizedSelections?.flavors || [])]
        .sort((a, b) => a.modifierId.localeCompare(b.modifierId))
        .map(f => f.modifierId)
        .join(',');

      const portions = [...(item.personalizedSelections?.portions || [])]
        .sort((a, b) => (a.portionId || '').localeCompare(b.portionId || ''))
        .map(p => `${p.portionId}:${p.optionId}`)
        .join('|');

      const sizeId =
        item.personalizedSelections?.selectedSize ||
        item.selectedVariation?.id ||
        'default';

      const mgKey = encodeMGS(item.modifierGroupSelections);

      return `${baseKey}:personalized:${sizeId}:${flavors || portions}:${mgKey}`;
    }

    default:
      return baseKey;
  }
};
